import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import axios from "axios";
import { toast } from "sonner";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  BarChart3,
  Brain,
  Building2,
  CheckCircle2,
  CircleAlert,
  CloudUpload,
  FileSpreadsheet,
  FileText,
  Gauge,
  Layers,
  LineChart,
  Loader2,
  Lock,
  Mail,
  Send,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  Target,
  TrendingUp,
  Users,
  Workflow,
  X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import {
  aggregateDrivers,
  GROUP_META,
  normalizeCode,
} from "../lib/denialMapping";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROW_LIMIT = 150;

/* ----------------------- PHI detection ----------------------- */
const PHI_HEADER_TOKENS = [
  "name", "first_name", "last_name", "patient_name", "patient", "full_name",
  "dob", "date_of_birth", "birthdate", "birth_date",
  "ssn", "social_security", "social_security_number",
  "mrn", "medical_record", "medical_record_number",
  "patient_id", "member_id", "subscriber_id", "subscriber",
  "account_number", "guarantor",
  "address", "street", "street_address", "zip", "zip_code", "postal_code",
  "phone", "phone_number", "telephone", "mobile",
  "email", "email_address",
];
const PHI_PREFIXES = ["patient_", "subscriber_", "member_", "guarantor_", "insured_"];
const PHI_SUFFIXES = ["_ssn", "_dob", "_mrn"];

const normHeader = (s) =>
  String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

const detectPhiHeaders = (headers) => {
  const hit = new Set();
  for (const h of headers) {
    const n = normHeader(h);
    if (!n) continue;
    if (PHI_HEADER_TOKENS.includes(n)) hit.add(h);
    else if (PHI_PREFIXES.some((p) => n.startsWith(p))) hit.add(h);
    else if (PHI_SUFFIXES.some((s) => n.endsWith(s))) hit.add(h);
  }
  return [...hit];
};

// Cell-content PHI scan — only highly distinctive patterns (SSN, email, phone).
// DOB regex is intentionally NOT included because YYYY-MM-DD / MM-DD-YYYY also
// matches legitimate service_date / dos / claim_date columns. DOB is still
// caught via the header detector when a column is explicitly named dob.
const SSN_RE = /\b\d{3}-?\d{2}-?\d{4}\b/;
const PHONE_RE = /\(?\b\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/;
const EMAIL_RE = /\b[\w._%+-]+@[\w.-]+\.[A-Za-z]{2,}\b/;

const detectPhiInCells = (rows) => {
  const findings = new Set();
  let scanned = 0;
  outer: for (const row of rows.slice(0, 80)) {
    for (const cell of row) {
      scanned += 1;
      if (scanned > 800) break outer;
      const v = String(cell ?? "").trim();
      if (!v) continue;
      // SSN: distinctive XXX-XX-XXXX pattern — but skip purely numeric phone-like
      // numbers; SSN_RE already requires the 3-2-4 grouping.
      if (SSN_RE.test(v) && !PHONE_RE.test(v)) findings.add("SSN");
      if (EMAIL_RE.test(v)) findings.add("email");
      // Phone: only flag if value looks like a phone number AND contains a real
      // delimiter (so 4-digit denial codes like 12345 don't trigger).
      if (PHONE_RE.test(v) && /[().\s-]/.test(v) && v.length >= 10) findings.add("phone");
      if (findings.size >= 3) break outer;
    }
  }
  return [...findings];
};

/* --------------------- Column-mapping ------------------------ */
// Aliases are matched against normalized (lowercase, alphanumeric-only)
// header strings using "equals" OR "contains" — so "Denial Code", "DenialCd",
// "code__denial", "DENIALCODE1" all match the "carc" group.
const COLUMN_GUESSES = {
  carc: [
    "carc", "carccode", "ansi", "ansicode", "denialcode", "denialcd", "denialcodes",
    "reasoncode", "rsncd", "adjustmentcode", "adjcode", "code", "codes",
    "denyreasoncode", "denyreason", "denyreasoncd", "denialreasoncode",
    "carcrarc", "rejectioncode", "groupcode", "rsncode", "denialid",
  ],
  rarc: [
    "rarc", "rarccode", "remarkcode", "remarkcd", "remark", "remarks",
    "remitremark", "remitremarkcode",
  ],
  dos: [
    "dos", "servicedate", "dateofservice", "servicedt", "fromdos", "fromdate",
    "svcdate", "datesvc", "startdate", "fromservicedate",
  ],
  payor: [
    "payor", "payer", "insurance", "insuranceplan", "plan", "carrier",
    "payorname", "payername", "ins", "insname", "primarypayor", "primarypayer",
  ],
  cpt: [
    "cpt", "cptcode", "procedure", "procedurecode", "hcpcs", "proccode",
    "proc",
  ],
  billed: [
    "billed", "billedamount", "chargeamount", "charges", "totalcharges",
    "charge", "billedchg", "billamt", "totalbilled", "grosschg", "amount",
    "amt", "claimamount", "totalamount", "totalclaimcharge",
  ],
  denied: [
    "denied", "deniedamount", "denyamount", "denialamount", "deniedamt",
    "rejectedamount", "writeoff", "writeoffamount",
  ],
  balance: [
    "balance", "openbalance", "arbalance", "outstanding", "remainingbalance",
    "balancedue", "currentbalance",
  ],
  description: [
    "description", "denialreason", "reason", "denialdescription",
    "remarkdescription", "denialtext", "denialmessage", "comments",
    "notes", "denyreason", "denialnarrative", "narrative", "rsndesc",
    "reasontext", "reasondescription",
  ],
};

const mapColumns = (headers) => {
  const norm = headers.map((h) => ({ raw: h, n: normHeader(h) }));
  const found = {};
  for (const [key, guesses] of Object.entries(COLUMN_GUESSES)) {
    found[key] = -1;
    // Pass 1: exact normalized match (highest confidence)
    for (let i = 0; i < norm.length; i++) {
      if (guesses.some((g) => norm[i].n === g)) { found[key] = i; break; }
    }
    // Pass 2: substring match
    if (found[key] === -1) {
      for (let i = 0; i < norm.length; i++) {
        if (guesses.some((g) => norm[i].n.includes(g))) { found[key] = i; break; }
      }
    }
  }
  return found;
};

/* ----------------------- Parse helpers ----------------------- */
const splitCsvLine = (line) => {
  const out = [];
  let cur = "";
  let q = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (q && line[i + 1] === '"') { cur += '"'; i++; }
      else q = !q;
    } else if (ch === "," && !q) {
      out.push(cur); cur = "";
    } else cur += ch;
  }
  out.push(cur);
  return out.map((s) => s.trim());
};

const parseCsv = (text) => {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (!lines.length) return { headers: [], rows: [] };
  const headers = splitCsvLine(lines[0]);
  const rows = lines.slice(1).map(splitCsvLine);
  return { headers, rows };
};

const parseXlsx = async (file) => {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", raw: false });
  if (!rows.length) return { headers: [], rows: [] };
  const headers = rows[0].map((h) => String(h ?? "").trim());
  const data = rows.slice(1).filter((r) => r.some((c) => String(c ?? "").trim() !== ""));
  return { headers, rows: data };
};

/* --------------------- Build denials ------------------------- */
const num = (s) => {
  const v = parseFloat(String(s ?? "0").replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(v) ? v : 0;
};
const splitCodes = (s) =>
  String(s ?? "")
    .split(/[;,\s|]+/)
    .map((x) => x.trim())
    .filter(Boolean);

// Pattern matches things like CO-16, CO16, PR1, OA-23, N210, M76, MA01, RARC123.
const CODE_PATTERN = /\b(CO|OA|PI|PR|CR|N|M|MA|RARC)[-_ ]?\d{1,4}\b/gi;

const sweepRowForCodes = (rowCells) => {
  const codes = [];
  for (const cell of rowCells) {
    const s = String(cell ?? "");
    if (!s) continue;
    const matches = s.match(CODE_PATTERN);
    if (matches) {
      for (const m of matches) codes.push(m);
    }
  }
  return codes;
};

const buildDenials = (headers, rows, cols) => {
  const haveCodeColumn = cols.carc >= 0 || cols.rarc >= 0;
  return rows.map((r) => {
    const codes = [];
    if (cols.carc >= 0) splitCodes(r[cols.carc]).forEach((c) => codes.push(c));
    if (cols.rarc >= 0) splitCodes(r[cols.rarc]).forEach((c) => codes.push(c));
    // Fallback: if no recognized code columns OR they came back empty,
    // sweep every cell in the row for code-shaped patterns.
    if (codes.length === 0 || !haveCodeColumn) {
      sweepRowForCodes(r).forEach((c) => codes.push(c));
    }
    const description = cols.description >= 0 ? String(r[cols.description] ?? "") : "";
    const amount = cols.denied >= 0 ? num(r[cols.denied]) : cols.billed >= 0 ? num(r[cols.billed]) : 0;
    const billed = cols.billed >= 0 ? num(r[cols.billed]) : amount;
    const balance = cols.balance >= 0 ? num(r[cols.balance]) : 0;
    const payor = cols.payor >= 0 ? String(r[cols.payor] ?? "") : "";
    const cpt = cols.cpt >= 0 ? String(r[cols.cpt] ?? "") : "";
    return { codes, description, amount, billed, balance, payor, cpt };
  });
};

const topCarcRarcCombos = (denials) => {
  const counts = new Map();
  for (const d of denials) {
    if (!d.codes.length) continue;
    const key = d.codes.map(normalizeCode).filter(Boolean).slice(0, 3).join(" + ");
    if (!key) continue;
    const prev = counts.get(key) || { count: 0, dollars: 0 };
    prev.count += 1;
    prev.dollars += d.amount;
    counts.set(key, prev);
  }
  return [...counts.entries()]
    .map(([combo, v]) => ({ combo, count: v.count, dollars: Math.round(v.dollars) }))
    .sort((a, b) => b.count - a.count || b.dollars - a.dollars)
    .slice(0, 5);
};

/* ----------------------- Helpers ----------------------------- */
const getFingerprint = () => {
  try {
    let fp = localStorage.getItem("rciq_fp");
    if (!fp) {
      fp = `fp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
      localStorage.setItem("rciq_fp", fp);
    }
    return fp;
  } catch {
    return `fp-${Date.now()}`;
  }
};

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const fmt$ = (n) => `$${fmt(n)}`;

/* =========================================================== */
const AIPreview = ({ onBookDemo }) => {
  const inputRef = useRef(null);
  const [email, setEmail] = useState("");
  const [ack, setAck] = useState(false);
  const [drag, setDrag] = useState(false);
  const [status, setStatus] = useState("idle"); // idle|scanning|done|rejected|exceeded|blocked
  const [errorDetail, setErrorDetail] = useState(null);
  const [fileMeta, setFileMeta] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [checking, setChecking] = useState(false);

  // On mount: don't pre-block. The full eligibility check runs once the user
  // enters a valid email (allowlisted emails bypass the check entirely).
  // The previous fingerprint-only pre-check was too aggressive and blocked
  // allowlisted users before they had a chance to enter their email.

  // Re-check whenever the user finishes typing a valid email
  useEffect(() => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    const fp = getFingerprint();
    const t = setTimeout(() => {
      axios
        .post(`${API}/preview/eligibility`, { email, fingerprint: fp })
        .then((res) => {
          if (res.data?.allowed) {
            // Allowed: clear any stale "blocked" state from a prior session.
            setStatus((s) => (s === "blocked" ? "idle" : s));
          } else {
            setStatus("blocked");
          }
        })
        .catch(() => {});
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const canUpload =
    status === "idle" &&
    ack &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  /* ------------ Eligibility per email + fp + IP ------------- */
  const verifyEligibility = async () => {
    setChecking(true);
    try {
      const fp = getFingerprint();
      const res = await axios.post(`${API}/preview/eligibility`, { email, fingerprint: fp });
      return res.data?.allowed === true;
    } catch {
      // network failure — allow but warn
      return true;
    } finally {
      setChecking(false);
    }
  };

  /* ------------------- File handling ------------------------ */
  const reset = () => {
    setStatus("idle");
    setErrorDetail(null);
    setAnalysis(null);
    setFileMeta(null);
  };

  const handleFile = async (file) => {
    if (!canUpload) {
      toast.error("Please enter your email address and confirm no PHI first.");
      return;
    }

    const allowed = await verifyEligibility();
    if (!allowed) {
      setStatus("blocked");
      return;
    }

    const lower = file.name.toLowerCase();
    if (!/\.(csv|xlsx|xls)$/.test(lower)) {
      toast.error("Only CSV or Excel files are accepted.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File too large. Preview accepts files under 8 MB.");
      return;
    }

    setFileMeta({ name: file.name, size: file.size });
    setStatus("scanning");

    try {
      const { headers, rows } =
        lower.endsWith(".csv") ? parseCsv(await file.text()) : await parseXlsx(file);

      // Hard row limit
      if (rows.length > ROW_LIMIT) {
        setStatus("exceeded");
        return;
      }

      // PHI scan — headers + cell content
      const phiHeaders = detectPhiHeaders(headers);
      const phiCells = detectPhiInCells(rows);
      if (phiHeaders.length || phiCells.length) {
        setErrorDetail({ headers: phiHeaders, cells: phiCells });
        setStatus("rejected");
        return;
      }

      // Column mapping + analysis
      const cols = mapColumns(headers);
      const denials = buildDenials(headers, rows, cols);
      const agg = aggregateDrivers(denials, 5);

      // Top 5 carc/rarc combos
      const combos = topCarcRarcCombos(denials);

      // Recoverable estimate = sum(driver.dollars * driver.recovery)
      const recoverable = agg.drivers.reduce(
        (s, d) => s + d.dollars * d.recovery,
        0,
      );
      const atRisk = agg.totals.totalDollars - recoverable;

      const actionPlan = agg.drivers.slice(0, 4).map((d) => ({
        group: d.group,
        action: GROUP_META[d.group]?.action || "Review this driver with your denial team.",
      }));

      setAnalysis({
        rowsAnalyzed: rows.length,
        totalDollars: Math.round(agg.totals.totalDollars),
        recoverable: Math.round(recoverable),
        atRisk: Math.round(atRisk),
        topDrivers: agg.drivers,
        combos,
        actionPlan,
        topDriverGroup: agg.drivers[0]?.group || "Other",
        cols,
      });
      setStatus("done");

      // Mark preview as used (no file content uploaded)
      try {
        await axios.post(`${API}/preview/complete`, {
          email,
          fingerprint: getFingerprint(),
          rows_analyzed: rows.length,
        });
      } catch {
        // non-fatal
      }
    } catch (err) {
      console.error(err);
      toast.error("Could not read that file. Try a CSV export.");
      setStatus("idle");
    }
  };

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDrag(false);
      const f = e.dataTransfer.files?.[0];
      if (f) handleFile(f);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canUpload, email, ack],
  );

  /* ======================== RENDER ========================= */
  return (
    <section id="ai-preview" data-testid="ai-preview-section" className="relative py-24 md:py-28">
      <div className="rciq-aurora opacity-50" />
      <div className="max-w-6xl mx-auto px-6 lg:px-8 relative">
        {/* HEADER */}
        <div className="max-w-3xl">
          <div className="rciq-chip">
            <Sparkles className="w-3.5 h-3.5" /> Free AI Preview · One per organization
          </div>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white">
            Try The AI Denial Intelligence Preview.
          </h2>
          <p className="mt-4 text-slate-300 leading-relaxed">
            Upload a fully de-identified denial export (CSV or Excel, up to {ROW_LIMIT} rows)
            and we'll run the AI denial-intelligence dashboard on your own data — KPIs,
            top drivers, root causes, recoverable revenue, and an action plan — in seconds.
            No PHI. No EHR integration.
          </p>
        </div>

        {/* PHI WARNING */}
        <div
          data-testid="phi-warning-banner"
          className="mt-8 rounded-xl border border-rose-500/40 bg-rose-500/[0.07] p-5 flex gap-3"
        >
          <ShieldAlert className="w-5 h-5 text-rose-300 shrink-0 mt-0.5" />
          <div className="text-[13.5px] text-rose-100/90 leading-relaxed">
            <span className="font-semibold text-rose-200">DO NOT upload PHI.</span>{" "}
            Patient names, DOB, member IDs, SSNs, addresses, phone numbers, MRNs,
            account numbers, or any other protected health information are
            prohibited. This preview tool only accepts <b>fully de-identified
            denial data</b>. Files containing PHI are automatically rejected and
            never stored.
          </div>
        </div>

        {/* MAIN PANEL */}
        <div className="mt-6 rciq-glass-strong p-6 sm:p-8">
          {/* BLOCKED */}
          {status === "blocked" && (
            <BlockedView onBookDemo={onBookDemo} />
          )}

          {/* IDLE / READY */}
          {status !== "blocked" && status === "idle" && (
            <>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="preview-email" className="text-slate-300 text-sm">Email Address</Label>
                  <Input
                    id="preview-email"
                    type="email"
                    data-testid="preview-email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@yourorg.com"
                    className="mt-1.5 h-11 bg-white/[0.04] border-white/10 text-white placeholder:text-slate-500"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox
                      data-testid="preview-ack-checkbox"
                      checked={ack}
                      onCheckedChange={(v) => setAck(v === true)}
                      className="mt-0.5 border-white/20 data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500"
                    />
                    <span className="text-[13px] text-slate-300 leading-snug">
                      I confirm this file contains <b className="text-white">NO PHI</b> and
                      is fully de-identified.
                    </span>
                  </label>
                </div>
              </div>

              {/* Dropzone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                onDragLeave={() => setDrag(false)}
                onDrop={onDrop}
                onClick={() => canUpload && inputRef.current?.click()}
                data-testid="preview-dropzone"
                className={`mt-6 cursor-pointer rounded-2xl border-2 border-dashed transition-all p-10 text-center ${
                  canUpload
                    ? drag
                      ? "border-cyan-400 bg-cyan-400/10"
                      : "border-white/15 bg-white/[0.02] hover:border-white/30"
                    : "border-white/8 bg-white/[0.02] opacity-60 cursor-not-allowed"
                }`}
              >
                <div className="w-14 h-14 mx-auto rounded-xl bg-cyan-400/10 border border-cyan-400/30 grid place-items-center text-cyan-300">
                  <CloudUpload className="w-6 h-6" />
                </div>
                <div className="mt-4 font-display text-lg font-semibold text-white">
                  Drop a de-identified denial file here
                </div>
                <div className="mt-1 text-sm text-slate-400">
                  CSV or Excel · up to {ROW_LIMIT} rows · max 8 MB
                </div>
                <div className="mt-4 inline-flex items-center gap-2 text-[12px] text-slate-500">
                  <FileSpreadsheet className="w-3.5 h-3.5" /> .csv · .xlsx
                </div>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="hidden"
                  data-testid="preview-file-input"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFile(f);
                  }}
                />
              </div>

              {!canUpload && (
                <div className="mt-4 text-[12px] text-slate-500 flex items-center gap-2">
                  <CircleAlert className="w-3.5 h-3.5" />
                  Enter your email address and confirm no PHI before uploading.
                </div>
              )}

              <div className="mt-4 flex flex-wrap items-center gap-2 justify-between">
                <div className="text-[12px] text-slate-400 inline-flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-cyan-300" />
                  Don&apos;t have a file ready? Try our perfect sample.
                </div>
                <a
                  href="/rciq-sample-denials.csv"
                  download="rciq-sample-denials.csv"
                  data-testid="preview-download-sample"
                  className="inline-flex items-center gap-2 text-[12px] font-semibold text-cyan-300 hover:text-cyan-200 transition px-3 py-1.5 rounded-lg border border-cyan-400/30 hover:border-cyan-400/60 bg-cyan-400/[0.05]"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5" />
                  Download sample CSV
                </a>
              </div>

              {checking && (
                <div className="mt-3 text-[12px] text-slate-400 flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Verifying eligibility…
                </div>
              )}
            </>
          )}

          {/* SCANNING */}
          {status === "scanning" && (
            <div data-testid="preview-scanning" className="py-16 text-center">
              <Loader2 className="w-8 h-8 mx-auto animate-spin text-cyan-300" />
              <div className="mt-5 font-display text-base font-medium text-white">
                Scanning {fileMeta?.name} for PHI…
              </div>
              <div className="mt-1 text-sm text-slate-400">
                Then analyzing ANSI/CARC/RARC, root causes, and recoverable revenue.
              </div>
            </div>
          )}

          {/* PHI REJECTION */}
          {status === "rejected" && (
            <div data-testid="preview-rejected" className="rounded-xl border border-rose-500/40 bg-rose-500/[0.07] p-5">
              <div className="flex items-start gap-3">
                <ShieldAlert className="w-5 h-5 text-rose-300 shrink-0 mt-0.5" />
                <div>
                  <div className="font-display font-semibold text-rose-100">
                    Upload rejected. Potential PHI detected.
                  </div>
                  <div className="mt-1 text-sm text-rose-100/80 leading-relaxed">
                    Please upload a fully de-identified denial file.
                    {errorDetail?.headers?.length ? (
                      <>
                        {" "}Found PHI-like column(s):{" "}
                        <span className="font-mono-rc text-rose-200">
                          {errorDetail.headers.join(", ")}
                        </span>.
                      </>
                    ) : null}
                    {errorDetail?.cells?.length ? (
                      <>
                        {" "}Cell-content patterns detected:{" "}
                        <span className="font-mono-rc text-rose-200">
                          {errorDetail.cells.join(", ")}
                        </span>.
                      </>
                    ) : null}
                  </div>
                  <button
                    onClick={reset}
                    data-testid="preview-rejected-retry"
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-rose-100 hover:text-white"
                  >
                    Try a different file <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* LIMIT EXCEEDED */}
          {status === "exceeded" && (
            <div data-testid="preview-exceeded" className="rounded-xl border border-amber-400/40 bg-amber-500/[0.07] p-5">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
                <div>
                  <div className="font-display font-semibold text-amber-100">
                    Preview limit exceeded.
                  </div>
                  <div className="mt-1 text-sm text-amber-100/80 leading-relaxed">
                    Maximum allowed for AI Preview is <b>{ROW_LIMIT}</b> denial records.
                    Trim your file and re-upload, or request enterprise access to analyze unlimited volume.
                  </div>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <button onClick={reset} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg rciq-btn-ghost text-sm">
                      Upload a smaller file
                    </button>
                    <button onClick={onBookDemo} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg rciq-btn-primary text-sm">
                      Request enterprise access <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* RESULTS */}
          {status === "done" && analysis && (
            <AnalysisView data={analysis} fileMeta={fileMeta} onBookDemo={onBookDemo} />
          )}
        </div>

        {/* Footer legal */}
        <div className="mt-6 grid sm:grid-cols-2 gap-3 text-[11.5px] text-slate-500">
          <div className="flex items-start gap-2">
            <Lock className="w-3.5 h-3.5 mt-0.5 text-cyan-300 shrink-0" />
            <p>
              Files are parsed in your browser. We never upload or persist your
              denial data; only an anonymized usage record (email, browser ID, IP
              hash, row count, timestamp) is kept to enforce one preview per org.
            </p>
          </div>
          <div className="flex items-start gap-2">
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-cyan-300 shrink-0" />
            <p>
              This preview tool is intended for operational and analytical
              purposes only. Users are responsible for ensuring all uploaded
              files are fully de-identified and HIPAA compliant.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------------- Sub-views ---------------------------- */
const BlockedView = ({ onBookDemo }) => (
  <div data-testid="preview-blocked" className="py-12 text-center">
    <div className="w-14 h-14 mx-auto rounded-full bg-cyan-400/10 border border-cyan-400/30 grid place-items-center text-cyan-300">
      <CheckCircle2 className="w-7 h-7" />
    </div>
    <h3 className="mt-5 font-display text-2xl font-semibold text-white">
      You've completed your AI Denial Intelligence Preview.
    </h3>
    <p className="mt-2 text-slate-300 max-w-md mx-auto">
      Contact Revenue Cycle IQ for full access — unlimited uploads, payer
      trending, AI workflows, and an executive dashboard for your team.
    </p>
    <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
      <button onClick={onBookDemo} className="h-11 px-5 rounded-lg rciq-btn-primary text-sm font-semibold inline-flex items-center gap-2">
        Contact Revenue Cycle IQ <ArrowRight className="w-4 h-4" />
      </button>
      <a href="mailto:intel@therevenuecycleiq.com" className="h-11 px-5 rounded-lg rciq-btn-ghost text-sm font-semibold inline-flex items-center gap-2">
        <Mail className="w-4 h-4 text-cyan-300" /> intel@therevenuecycleiq.com
      </a>
    </div>
  </div>
);

const AnalysisView = ({ data, fileMeta, onBookDemo }) => {
  return (
    <div data-testid="preview-result" className="space-y-6">
      <div className="flex items-start gap-3 rounded-xl border border-teal-400/30 bg-teal-500/[0.06] p-4">
        <CheckCircle2 className="w-5 h-5 text-teal-300 shrink-0 mt-0.5" />
        <div className="text-sm text-teal-100 leading-relaxed">
          <span className="font-semibold">Clean file accepted.</span> No PHI detected.
          Analyzed <b>{fmt(data.rowsAnalyzed)}</b> denial rows from <span className="font-mono-rc">{fileMeta?.name}</span>.
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Denied $ analyzed" value={fmt$(data.totalDollars)} tone="amber" Icon={AlertTriangle} />
        <Kpi label="Est. recoverable $" value={fmt$(data.recoverable)} tone="teal" Icon={TrendingUp} />
        <Kpi label="At-risk $" value={fmt$(data.atRisk)} tone="rose" Icon={ShieldAlert} />
        <Kpi label="Top denial driver" value={data.topDriverGroup} tone="cyan" Icon={Target} small />
      </div>

      {/* Top 5 drivers */}
      <div className="rciq-glass p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-400">Top 5 Denial Drivers</div>
            <div className="font-display text-base font-medium text-white">Operational groupings by $ at risk</div>
          </div>
          <span className="text-[11px] text-teal-300 font-mono-rc bg-teal-400/10 px-2 py-1 rounded">
            From your file
          </span>
        </div>
        <div className="space-y-2.5">
          {data.topDrivers.map((d, i) => (
            <div key={d.group} className="grid grid-cols-12 items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/[0.04] transition">
              <div className="col-span-5 flex items-center gap-2.5">
                <span className="font-mono-rc text-[10px] text-slate-500">0{i + 1}</span>
                <span className="text-sm text-white font-medium">{d.group}</span>
              </div>
              <div className="col-span-3 flex items-center gap-2">
                <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-cyan-400 to-teal-300" style={{ width: `${Math.min(100, d.pct * 3)}%` }} />
                </div>
                <span className="text-[11px] text-slate-400 font-mono-rc">{d.pct}%</span>
              </div>
              <div className="col-span-2 text-right text-[12px] text-slate-300 font-mono-rc">{d.count}</div>
              <div className="col-span-2 text-right font-mono-rc text-sm text-white font-semibold">{fmt$(d.dollars)}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 pt-3 border-t border-white/5 grid grid-cols-4 text-[10px] uppercase tracking-wide text-slate-500">
          <span>Driver</span><span>% of denials</span><span className="text-right">Count</span><span className="text-right">$ at risk</span>
        </div>
      </div>

      {/* Combos + Action Plan */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rciq-glass p-5">
          <div className="text-[11px] uppercase tracking-wide text-slate-400">Top 5 CARC / RARC Combinations</div>
          <div className="font-display text-base font-medium text-white mb-3">Recurring code patterns</div>
          {data.combos.length === 0 ? (
            <div className="text-sm text-slate-400">No CARC/RARC columns detected in your file. Add a column like <span className="font-mono-rc text-slate-200">denial_code</span> to surface this.</div>
          ) : (
            <ul className="space-y-2">
              {data.combos.map((c, i) => (
                <li key={c.combo} className="flex items-center justify-between text-sm py-1.5 border-b border-white/5 last:border-b-0">
                  <span className="flex items-center gap-2">
                    <span className="font-mono-rc text-[10px] text-slate-500">0{i + 1}</span>
                    <span className="font-mono-rc text-cyan-300 bg-cyan-400/10 px-1.5 py-0.5 rounded text-[11px]">{c.combo}</span>
                  </span>
                  <span className="flex items-center gap-3 text-[12px]">
                    <span className="text-slate-400 font-mono-rc">{c.count} claims</span>
                    <span className="text-white font-mono-rc font-semibold">{fmt$(c.dollars)}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rciq-glass-strong p-5 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-40"
               style={{ background: "radial-gradient(closest-side, rgba(45,212,191,0.6), transparent)" }} />
          <div className="relative">
            <div className="text-[11px] uppercase tracking-wide text-teal-300 font-semibold">AI Action Plan</div>
            <div className="font-display text-base font-medium text-white mb-3">Operational recommendations</div>
            <ul className="space-y-3">
              {data.actionPlan.map((a) => (
                <li key={a.group} className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-lg bg-cyan-400/10 border border-cyan-400/25 grid place-items-center text-cyan-300 shrink-0">
                    <Workflow className="w-3.5 h-3.5" />
                  </div>
                  <div>
                    <div className="text-sm text-white font-medium">{a.group}</div>
                    <div className="text-[12.5px] text-slate-300 leading-relaxed">{a.action}</div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-4 pt-3 border-t border-white/10 text-[11px] text-slate-500 leading-relaxed flex items-start gap-2">
              <CircleAlert className="w-3 h-3 mt-0.5 shrink-0" />
              AI recommendations are operational guidance only and do not guarantee reimbursement outcomes.
            </div>
          </div>
        </div>
      </div>

      {/* What you unlock in the full dashboard */}
      <div data-testid="dashboard-perks" className="rciq-glass-strong p-6 sm:p-7 relative overflow-hidden">
        <div className="absolute inset-0 opacity-80 pointer-events-none"
             style={{ background: "radial-gradient(60% 80% at 100% 0%, rgba(45,212,191,0.10) 0%, rgba(45,212,191,0) 60%), radial-gradient(50% 60% at 0% 100%, rgba(6,182,212,0.08) 0%, rgba(6,182,212,0) 60%)" }} />
        <div className="relative">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[10px] tracking-[0.22em] uppercase text-cyan-300 font-bold">A preview of the full platform</span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-400/15 border border-cyan-400/30 text-[9px] uppercase tracking-wider text-cyan-200 font-semibold">
              <Lock className="w-2.5 h-2.5" /> Unlocked with subscription
            </span>
          </div>
          <h3 className="font-display text-xl sm:text-2xl font-semibold text-white tracking-tight">
            In the full dashboard, you also get…
          </h3>
          <p className="mt-2 text-slate-300 text-[13.5px] leading-relaxed max-w-3xl">
            This preview shows a slice of what your team gets every day inside the Revenue Cycle IQ dashboard — payor-aware
            denial intelligence, recoverability scoring, and orchestrated appeal workflows. Here&apos;s what unlocks when you onboard:
          </p>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              {
                Icon: Building2,
                title: "Payor Performance Scorecards",
                body: "Side-by-side denial rate, average days to denial, recovery rate, and $ at risk for every payor in your book.",
              },
              {
                Icon: Gauge,
                title: "Per-Claim Recoverability Score",
                body: "AI ranks every denied claim by likelihood of recovery × dollar value — so your team works the right ones first.",
              },
              {
                Icon: Workflow,
                title: "AI Appeal Workflows",
                body: "Payor-aware appeal templates, escalation paths, and reminders — orchestrated automatically for each denial cohort.",
              },
              {
                Icon: LineChart,
                title: "Denial Trending Over Time",
                body: "Rolling 30/60/90-day trendlines per CARC code, payor, specialty, and place of service. Spot upstream issues early.",
              },
              {
                Icon: Stethoscope,
                title: "Multi-Specialty Insights",
                body: "Specialty-aware refinements for hospital, professional, ambulatory, surgical, and ancillary services — same engine, smarter context.",
              },
              {
                Icon: Layers,
                title: "Root-Cause Mapping",
                body: "Denials grouped by upstream operational driver (eligibility, authorization, coding, charge capture) — not just by ANSI code.",
              },
              {
                Icon: FileText,
                title: "Executive-Ready PDF Reports",
                body: "Board-ready scorecards, monthly executive summaries, and payer review packets — generated on-demand with one click.",
              },
              {
                Icon: Users,
                title: "Team Seats & Role-Based Access",
                body: "Invite your billers, denial specialists, and revenue cycle leadership — each with the right level of visibility.",
              },
              {
                Icon: Activity,
                title: "Continuous Human-Supervised AI",
                body: "Unlike generic tools, our AI is continuously refined by working RCM experts — recommendations stay sharp and operationally credible.",
              },
            ].map((p) => (
              <div key={p.title} className="rounded-xl border border-white/10 bg-white/[0.025] p-4 hover:border-cyan-400/40 hover:bg-cyan-400/[0.04] transition group">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-cyan-400/10 border border-cyan-400/25 grid place-items-center text-cyan-300">
                    <p.Icon className="w-4 h-4" />
                  </div>
                  <div className="text-sm font-semibold text-white tracking-tight leading-tight">{p.title}</div>
                </div>
                <div className="text-[12.5px] text-slate-400 leading-relaxed">{p.body}</div>
              </div>
            ))}
          </div>

          <div className="mt-5 pt-4 border-t border-white/5 flex flex-wrap items-center justify-between gap-3">
            <div className="text-[12px] text-slate-400">
              <span className="text-cyan-300 font-semibold">Ready for the full picture?</span>{" "}
              Book a 20-minute live walkthrough with our team.
            </div>
            <button
              onClick={onBookDemo}
              data-testid="dashboard-perks-cta"
              className="h-10 px-4 rounded-lg rciq-btn-primary text-[13px] font-semibold inline-flex items-center gap-2"
            >
              <Sparkles className="w-3.5 h-3.5" /> See the full dashboard
            </button>
          </div>
        </div>
      </div>

      {/* Final CTAs */}
      <div className="rounded-2xl rciq-glass-strong p-6 sm:p-8 relative overflow-hidden">
        <div className="absolute inset-0 opacity-90 pointer-events-none"
             style={{ background: "radial-gradient(60% 50% at 80% 0%, rgba(45,212,191,0.18) 0%, rgba(45,212,191,0) 60%), radial-gradient(50% 40% at 10% 100%, rgba(6,182,212,0.18) 0%, rgba(6,182,212,0) 60%)" }} />
        <div className="relative grid md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-8">
            <div className="text-[11px] tracking-[0.22em] uppercase text-cyan-300 font-semibold">Want more?</div>
            <h3 className="mt-2 font-display text-2xl sm:text-3xl font-semibold text-white tracking-tight">
              Want Full Denial Intelligence, Payer Trending, AI Workflows, And Unlimited Uploads?
            </h3>
            <p className="mt-2 text-slate-300 text-sm leading-relaxed">
              Get the full Revenue Cycle IQ platform — built for revenue cycle leaders who need answers, not another spreadsheet.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-col gap-2.5">
            <button onClick={onBookDemo} data-testid="preview-cta-book-demo" className="h-11 px-5 rounded-lg rciq-btn-primary text-sm font-semibold inline-flex items-center justify-center gap-2">
              Book A Demo <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={onBookDemo} data-testid="preview-cta-enterprise" className="h-11 px-5 rounded-lg rciq-btn-ghost text-sm font-semibold inline-flex items-center justify-center gap-2">
              Request Enterprise Access
            </button>
            <a
              href="mailto:intel@therevenuecycleiq.com"
              data-testid="preview-cta-contact"
              className="h-11 px-5 rounded-lg rciq-btn-ghost text-sm font-semibold inline-flex items-center justify-center gap-2"
            >
              <Mail className="w-4 h-4 text-cyan-300" /> Contact Revenue Cycle IQ
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const Kpi = ({ label, value, tone, Icon, small }) => {
  const toneCls = {
    teal:  "text-teal-300 bg-teal-400/10 border-teal-400/25",
    rose:  "text-rose-300 bg-rose-500/10 border-rose-500/25",
    amber: "text-amber-300 bg-amber-500/10 border-amber-500/25",
    cyan:  "text-cyan-300 bg-cyan-400/10 border-cyan-400/25",
  }[tone] || "text-slate-200 bg-white/[0.05] border-white/10";
  return (
    <div className={`rciq-glass p-4 border ${toneCls.split(" ").filter(c => c.startsWith("border")).join(" ")}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wide text-slate-400">{label}</span>
        <Icon className={`w-3.5 h-3.5 ${toneCls.split(" ").filter(c => c.startsWith("text")).join(" ")}`} />
      </div>
      <div className={`mt-1.5 font-display ${small ? "text-base" : "text-2xl"} font-semibold text-white truncate`}>
        {value}
      </div>
    </div>
  );
};

export default AIPreview;
